import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { RetentionStatusResponse } from '../../interfaces';
import { RetentionBackendService } from '../../backend-services/retention/retention.backend-service';
import { MdbToastService } from '../mdb-toast/mdb-toast.service';
import { SocketIoService } from '../socket-io/socket-io.service';
import { MyUserService } from '../my-user/my-user.service';

/** "1.240" — dot grouping as Austrians write coin amounts (ICU de-AT would use a space). */
export function formatCoins(value: number): string {
  return Math.round(value || 0).toLocaleString('de-DE');
}

function validCoins(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0;
}

/**
 * Live coins / daily bonus / daily tasks / weekly goal for the lobby bar.
 * Root singleton: it subscribes to the backend's retention socket messages exactly once
 * (SocketIoService has no `off`), shows the reward toasts everywhere in the app (also
 * on the end-of-game screen) and refreshes the status after each event.
 *
 * Socket messages (backend RetentionService.trackEvent):
 *  - `challengeCompleted` { title, reward, coinsEarned, newBalance }
 *  - `challengeProgress`  { title, progress, target }
 *  - `dailyBonusCredited` { coinsEarned, newBalance, streakDay }
 */
@Injectable({ providedIn: 'root' })
export class RetentionLiveService {
  private readonly statusSource = new BehaviorSubject<RetentionStatusResponse | null>(null);
  public readonly status$: Observable<RetentionStatusResponse | null> = this.statusSource.asObservable();
  private refreshTimer: any = null;
  /** bumped on every identity change: a status request of the previous account is dropped */
  private sessionVersion = 0;
  /** several messages arrive per finished game — one status request for all of them */
  static readonly REFRESH_DEBOUNCE_MS = 400;

  constructor(
    private backend: RetentionBackendService,
    private toastService: MdbToastService,
    socket: SocketIoService,
    myUser: MyUserService
  ) {
    // Coins and tasks belong to one account: never show them to the next one.
    myUser.reset$.subscribe(() => this.clear());
    socket.on('challengeCompleted', (message: any) => this.onChallengeCompleted(message));
    socket.on('challengeProgress', (message: any) => {
      if (message?.success === true) this.scheduleRefresh();
    });
    socket.on('dailyBonusCredited', (message: any) => this.onDailyBonusCredited(message));
  }

  public get status(): RetentionStatusResponse | null {
    return this.statusSource.value;
  }

  /** Loads the status; a failed request keeps the last known state. */
  public async refresh(): Promise<void> {
    const version = this.sessionVersion;
    try {
      const status: any = await this.backend.getStatus();
      if (version !== this.sessionVersion) return;
      // Without a session the endpoint answers 200 with { error } — no bar then.
      this.statusSource.next(status && validCoins(status.coins) ? status : null);
    } catch (error) {
      console.warn('Could not load retention status:', error);
    }
  }

  /** Drops the status and any pending or running refresh (logout / account switch). */
  public clear(): void {
    this.sessionVersion++;
    clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
    this.statusSource.next(null);
  }

  private scheduleRefresh(): void {
    clearTimeout(this.refreshTimer);
    this.refreshTimer = setTimeout(() => {
      this.refreshTimer = null;
      void this.refresh();
    }, RetentionLiveService.REFRESH_DEBOUNCE_MS);
  }

  /** Shows the new balance right away; the debounced refresh brings tasks/goal up to date. */
  private applyBalance(balance: unknown): void {
    const status = this.statusSource.value;
    if (status && validCoins(balance)) this.statusSource.next({ ...status, coins: balance });
  }

  private onChallengeCompleted(message: any): void {
    if (message?.success !== true || !message.data) return;
    const { title, reward, coinsEarned, newBalance } = message.data;
    const coins = coinsEarned ?? reward;
    if (typeof title !== 'string' || !title.trim() || !validCoins(coins)) return;
    this.toastService.success(`Aufgabe erledigt: ${title.trim()}, +${formatCoins(coins)} Münzen`);
    this.applyBalance(newBalance);
    this.scheduleRefresh();
  }

  private onDailyBonusCredited(message: any): void {
    if (message?.success !== true || !message.data) return;
    const { coinsEarned, newBalance, streakDay } = message.data;
    if (!validCoins(coinsEarned)) return;
    const days = Number.isInteger(streakDay) && streakDay > 0 ? streakDay : 1;
    this.toastService.success(
      `Tagesbonus: +${formatCoins(coinsEarned)} Münzen (Serie ${days} ${days === 1 ? 'Tag' : 'Tage'})`
    );
    const status = this.statusSource.value;
    if (status) {
      this.statusSource.next({
        ...status,
        coins: validCoins(newBalance) ? newBalance : status.coins,
        daily_bonus: { credited: true, reward: coinsEarned, streak_day: days },
      });
    }
    this.scheduleRefresh();
  }
}
